import { ChangeDetectionStrategy, Component, Input, OnInit } from "@angular/core";
import { UntypedFormArray, UntypedFormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { isEmpty, isNull } from "lodash";
import { switchMap } from "rxjs";
import { CandidateSignOffDetails, DialogData, OBComments, SignOffPopup } from "src/app/models/models";
import { CbtaModalComponent } from "src/app/shared/components/cbta-modal/cbta-modal.component";
import { DraftService } from "src/app/shared/services/draft.service";
import { HttpService } from "src/app/shared/services/http.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import { ProfileService } from "src/app/shared/services/profile.service";
import { RoleService } from "src/app/shared/services/role.service";
import { SharedService } from "src/app/shared/services/shared.service";

@Component({
	selector: "app-candidate-signoff-form",
	templateUrl: "./candidate-signoff-form.component.html",
	styleUrls: ["./candidate-signoff-form.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class CandidateSignoffFormComponent implements OnInit {
	signoffForm!: UntypedFormGroup;
	parentForm!: UntypedFormArray;
	parentFormArrayIndex: number = 0;
	panelOpenState: boolean = false;
	ratingAVal: number = 0;
	ratingBVal: number = 0;
	ratingCVal: number = 0;
	ratingFinalVal: number = 0;
	obComments: Array<OBComments> = [];

	@Input() index: number = 0;
	@Input("signoffForm") set _(value: any) {
		this.signoffForm = value as UntypedFormGroup;
	}

	instructorSignatureEditMode: boolean = true;
	traineeSignatureEditMode: boolean = true;
	instructorSignaturePreview?: string = "";
	traineeSignaturePreview?: string = "";
	instructorSignatureFile?: File;

	isOnline: boolean = true;

	constructor(
		public sharedService: SharedService,
		private dialog: MatDialog,
		private httpService: HttpService,
		private loadingService: LoaderService,
		private roleService: RoleService,
		private draftService: DraftService,
		private profileService: ProfileService,
	) {}

	ngOnInit(): void {
		this.sharedService.getIsStatusOnline().subscribe((status: boolean) => {
			console.log("header ngOnInit : ", status);
			this.isOnline = status;
		});

		this.parentForm = this.signoffForm.parent as UntypedFormArray;
		this.parentFormArrayIndex = this.parentForm.controls.findIndex((subform) => subform == this.signoffForm);

		this.ratingAVal = this.ratingA?.value;
		this.ratingBVal = this.ratingB?.value;
		this.ratingCVal = this.ratingC?.value;
		this.ratingFinalVal = this.finalRating?.value;

		this.category?.valueChanges.subscribe((value: any) => {
			this.updateAssessmentRecommendations({
				category: value ?? "",
			});

			if (value === "Check") {
				setTimeout(() => {
					this.clearanceCheck?.patchValue("N/A");
				}, 100);
			}
		});

		this.sessionCheck?.valueChanges.subscribe((value: any) => {
			this.updateAssessmentRecommendations({
				sessionCheck: value ?? "",
			});
		});

		this.objectiveCheck?.valueChanges.subscribe((value: any) => {
			this.updateAssessmentRecommendations({
				objectiveCheck: value ?? "",
			});
		});

		this.clearanceCheck?.valueChanges.subscribe((value: any) => {
			this.updateAssessmentRecommendations({
				clearanceCheck: value ?? "",
			});
		});

		//default check is true for instructor license/examiner number
		this.toggleLicenseNumber(true);
	}

	get indDraftMode(): boolean {
		return this.draftService.inDraftMode;
	}

	updateAssessmentRecommendations(data: Partial<CandidateSignOffDetails>) {
		if (this.indDraftMode) {
			const signOffDetails = this.draftService.draft?.signOffDetails;

			this.draftService.updateSelectedDraft({
				signOffDetails: {
					candidateSignOffDetails: {
						...signOffDetails?.candidateSignOffDetails,
						[this.candidateId.value]: {
							...signOffDetails?.candidateSignOffDetails[this.candidateId.value],
							...data,
						},
					},
				},
				continuePage: 3,
			});
		} else {
			const tmpCandidateSignOffDetails = this.draftService?.tempDraft?.signOffDetails?.candidateSignOffDetails ?? {};

			this.draftService.tempDraft = {
				signOffDetails: {
					candidateSignOffDetails: {
						...tmpCandidateSignOffDetails,
						[this.candidateId.value]: {
							...tmpCandidateSignOffDetails[this.candidateId.value],
							candidateId: this.candidateId.value,
							traineeAssessmentId: this.traineeAssessmentId.value,
							...data,
						},
					},
				},
			};
		}
	}

	get candidateName() {
		return this.signoffForm.get("candidateName")!;
	}

	get candidateId() {
		return this.signoffForm.get("candidateId")!;
	}

	get traineeAssessmentId() {
		return this.signoffForm.get("traineeAssessmentId")!;
	}

	get ratingA() {
		return this.signoffForm.get("ratingA");
	}

	get ratingB() {
		return this.signoffForm.get("ratingB");
	}

	get ratingC() {
		return this.signoffForm.get("ratingC");
	}

	get finalRating() {
		return this.signoffForm.get("finalRating");
	}

	get category() {
		return this.signoffForm.get("category")!;
	}

	get sessionCheck() {
		return this.signoffForm.get("sessionCheck");
	}

	get objectiveCheck() {
		return this.signoffForm.get("objectiveCheck");
	}

	get clearanceCheck() {
		return this.signoffForm.get("clearanceCheck");
	}

	get trainerName() {
		return this.signoffForm.get("trainerName");
	}

	get trainerSignature() {
		return this.signoffForm.get("trainerSignature");
	}

	get traineeName() {
		return this.signoffForm.get("traineeName");
	}

	get traineeSignature() {
		return this.signoffForm.get("traineeSignature");
	}

	get instructorLicenseNumber() {
		return this.signoffForm.get("instructorLicenseNumber");
	}

	get useInstructorLicense() {
		return this.signoffForm.get("useInstructorLicense");
	}

	toggleLicenseNumber(isLicenseNumber: boolean) {
		this.useInstructorLicense?.patchValue(isLicenseNumber);

		let number;
		if (this.useInstructorLicense?.value) {
			number = this.profileService?.profile?.pilot_license_number;
		} else {
			number = this.profileService?.profile?.examiner_number;
		}

		this.instructorLicenseNumber?.patchValue(number);

		this.updateAssessmentRecommendations({
			useInstructorLicense: this.useInstructorLicense?.value,
			instructorLicenseNumber: number,
		});
	}

	get instructorHasSignaturePreview(): boolean {
		return !isEmpty(this.instructorSignaturePreview) && !isNull(this.instructorSignaturePreview);
	}

	get traineeHasSignaturePreview(): boolean {
		return !isEmpty(this.traineeSignaturePreview) && !isNull(this.traineeSignaturePreview);
	}

	listallOBComments(val: OBComments) {
		this.obComments.push(val);
	}

	onInstructorSignatureUpdate(e: Event): void {
		const files = (e.target as HTMLInputElement)?.files ?? [];
		if (!isEmpty(files)) {
			this.trainerSignature?.patchValue(files[0]);
		}
	}

	onTraineeSignatureUpdate(e: Event): void {
		const files = (e.target as HTMLInputElement)?.files ?? [];
		if (!isEmpty(files)) {
			this.traineeSignature?.patchValue(files[0]);
		}
	}

	showInstructorSignatureModal(): void {
		const dialogRef = this.dialog.open(CbtaModalComponent, {
			panelClass: "signature-pad-modal",
			height: "420px",
			width: "528px",
			hasBackdrop: true,
			data: {
				modalTitle: "Signature",
				modalName: "signaturePadModal",
				modalBtnConfirmText: "Submit",
				modalBtnCancelText: "Cancel",
				signatureData: {
					dataUrl: this.profileService.signaturePreview,
					file: this.profileService.signatureFile,
				},
			},
		});

		const sub = dialogRef.afterClosed().subscribe((result: any) => {
			if (result && result.close) {
				const dataUrl = result?.signature?.dataUrl;
				const file = result?.signature?.file;

				this.trainerSignature?.patchValue(file);
				// this.parentForm.controls[1].get('trainerSignature')?.patchValue(file);

				this.draftService.syncSelectedDraft();

				if (dataUrl) {
					this.instructorSignaturePreview = result.signature.dataUrl;
				}
			}
			sub.unsubscribe();
		});
	}

	showTraineeSignatureModal(signatureBytes?: number[] | Uint8Array): void {
		const baseDialogData: DialogData = {
			modalTitle: "Signature",
			modalName: "signaturePadModal",
			modalBtnConfirmText: "Submit",
			modalBtnCancelText: "Cancel",
		};

		const openDialog = (dialogData: DialogData) => {
			const dialogRef = this.dialog.open(CbtaModalComponent, {
				panelClass: "signature-pad-modal",
				height: "420px",
				width: "528px",
				hasBackdrop: true,
				data: dialogData,
			});

			dialogRef.afterClosed().subscribe((result: any) => {
				if (result?.close) {
					const file = result?.signature?.file;
					const dataUrl = result?.signature?.dataUrl;

					this.traineeSignature?.patchValue(file);
					this.draftService.syncSelectedDraft();

					if (dataUrl) {
						this.traineeSignaturePreview = dataUrl;
					}
				}
			});
		};

		if (!signatureBytes) {
			openDialog(baseDialogData);
			return;
		}

		const uint8Array = new Uint8Array(signatureBytes);
		const blob = new Blob([uint8Array], { type: "image/png" });
		const file = new File([blob], "signature.png", { type: "image/png" });

		const reader = new FileReader();
		reader.onload = () => {
			const dataUrl = reader.result as string;

			openDialog({
				...baseDialogData,
				signatureData: { dataUrl, file },
			});
		};

		reader.readAsDataURL(blob);
	}

	get signingIsValid(): boolean {
		return (
			(this.category.valid && this.sessionCheck?.valid && this.objectiveCheck?.valid && this.clearanceCheck?.valid) ??
			false
		);
	}

	async signOffModel(user: string) {
		if (!navigator.onLine) {
			this.draftService.blockSubmitIfOffline(true);
			return;
		}

		this.draftService.syncSelectedDraft();

		const dialogRef = this.dialog.open(CbtaModalComponent, {
			panelClass: "signoff-modal",
			height: "400px",
			width: "460px",
			hasBackdrop: true,
			data: {
				modalName: "signOffModal",
				modalTitle: "Sign Off",
				modalContent: "",
				modalBtnConfirmText: "Sign",
				modalBtnCancelText: "Cancel",
				disableClose: true,
			},
		});

		const sub = dialogRef.afterClosed().subscribe((result: any) => {
			if (result) {
				const draftId = this.draftService.draft?.id;

				let signOffData = {
					candidateId: this.candidateId.value,
					instructorId: this.roleService.userDetails.id,
					secretKey: result.signOffData?.secretKey,
					staffNumber: result.signOffData?.staffNumber,
					...(draftId && { traineeAssessmentIdReference: draftId }),
				} as SignOffPopup;

				this.loadingService.setLoading(true);

				this.draftService.syncSelectedDraft();

				if (user === "trainer") {
					this.httpService.instructorSignoff(this.traineeAssessmentId.value, signOffData).subscribe(() => {
						this.loadingService.setLoading(false);
						this.showInstructorSignatureModal();
					});
				} else if (user === "trainee") {
					this.httpService
						.candidateSignoff(this.traineeAssessmentId.value, signOffData)
						.pipe(switchMap(() => this.httpService.candidateSignature(result.signOffData.staffNumber)))
						.subscribe({
							next: (data) => {
								this.loadingService.setLoading(false);
								const signatureBytes = data?.data?.signature?.data;

								this.showTraineeSignatureModal(signatureBytes);
							},
							error: () => {
								this.loadingService.setLoading(false);
								// optionally handle error here
							},
						});
				}
				this.loadingService.setLoading(false);
			}
			sub.unsubscribe();
		});
	}
}
